import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * SIMPLIFIED Custom Route Reuse Strategy
 * 
 * This is a minimal implementation that only caches specific non-mailbox routes.
 * Mailbox caching has been disabled to prevent lockup issues.
 * 
 * Routes that ARE cached:
 * - /orders/list
 * - /artwork/list
 * - /purchasing/list
 * 
 * Routes that are NOT cached (always fresh):
 * - ALL mailbox routes (to prevent state issues)
 */
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
    private handlers: Map<string, DetachedRouteHandle> = new Map();
    
    /**
     * Routes that should be cached (kept alive when navigating away)
     * NOTE: Mailbox routes are intentionally excluded to prevent lockup issues
     */
    private readonly routesToCache: string[] = [
        '/orders/list',
        '/artwork/list',
        '/purchasing/list'
    ];

    /**
     * Determines if this route should be detached to be reused later
     */
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        const path = this.getRoutePath(route);
        
        // NEVER cache mailbox routes - they have complex state that causes issues
        if (path.includes('/mailbox')) {
            return false;
        }
        
        // Only cache routes in the whitelist
        return this.routesToCache.some(cachedRoute => path.includes(cachedRoute));
    }

    /**
     * Stores the detached route
     */
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        if (handle) {
            const path = this.getRoutePath(route);
            // Only store non-mailbox routes
            if (!path.includes('/mailbox')) {
                this.handlers.set(path, handle);
            }
        }
    }

    /**
     * Determines if this route should be reattached
     */
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        const path = this.getRoutePath(route);
        
        // Never reattach mailbox routes
        if (path.includes('/mailbox')) {
            return false;
        }
        
        return this.handlers.has(path);
    }

    /**
     * Retrieves the previously stored route
     */
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        const path = this.getRoutePath(route);
        
        // Never retrieve mailbox routes from cache
        if (path.includes('/mailbox')) {
            return null;
        }
        
        return this.handlers.get(path) || null;
    }

    /**
     * Determines if a route should be reused
     */
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        // Default Angular behavior - reuse if route config is the same
        return future.routeConfig === curr.routeConfig;
    }
    
    /**
     * Get simple route path
     */
    private getRoutePath(route: ActivatedRouteSnapshot): string {
        // Build path from route tree
        const segments: string[] = [];
        let current: ActivatedRouteSnapshot | null = route;
        
        while (current) {
            if (current.routeConfig?.path) {
                segments.unshift(current.routeConfig.path);
            }
            current = current.parent;
        }
        
        return '/' + segments.join('/');
    }

    /**
     * Clear all cached routes
     */
    public clearCache(): void {
        this.handlers.clear();
    }
}

